/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { useState } from "react"
import { BaseButton, Grid } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { CustomerSelectList2 } from "../../../Components/SelectLists/CustomerSelectList2"
import { buttonConfigs } from "../../../constants/buttonConfigs"
import { useAsyncCallback } from "../../../hooks/useAsyncCallback"
import { FieldGroup } from "../../../Components/FieldGroup"
import { useSiteAlerts } from "../../../hooks/useSiteAlerts"
import { IComponentProp, titleize } from "@cd2/tools"
import { placeholders } from "../../../constants/placeholders"
import { countries } from "../../../constants/countries"
import { BranchSelect } from "../../../Components/SelectLists/BranchSelect"
import { IRecord } from "../../../types/schema"
import { ButtonsWrapper } from "../../../Components/ButtonsWrapper"

type IData = {
  id?: string
  company_name?: string
  name?: string
  email?: string
  phone?: string
  notes?: string
  source?: string
  status?: string
  customer?: { id: string } | null
  branch?: IRecord<"branches", "name"> | null
  created_by?: { id: string } | null
  address?: {
    id?: string
    line_1?: string
    line_2?: string
    line_3?: string
    city?: string
    county?: string
    country?: string
    postcode?: string
  }
}

const defaultData: IData = {
  company_name: "",
  name: "",
  email: "",
  phone: "",
  notes: "",
  source: "",
  status: "",
}

export function LeadsForm({
  initialData,
  onSubmit,
  disableBranchSelection,
  actions,
}: {
  initialData?: IData
  onSubmit(data: IData): Promise<void>
  disableBranchSelection?: boolean
  actions?: React.ReactNode
}) {
  const [data, setData] = useState<IData>({ ...defaultData, ...initialData })
  const [errors, setErrors] = useState<Partial<IData>>({})
  const alerts = useSiteAlerts()

  function handleChange(newData: Partial<IData>) {
    setData(data => ({ ...data, ...newData }))
  }

  const handleSubmit = useAsyncCallback(async () => {
    setErrors({})
    const errors = {} as Partial<IData>
    if (!data.name) {
      errors["name"] = "Name is required"
    }
    if (!data.email) {
      errors["email"] = "Email is required"
    }
    if (data.email && !data.email.includes("@")) {
      errors["email"] = "Email is invalid"
    }
    if (!data.phone) {
      errors["phone"] = "Phone is required"
    }
    setErrors(errors)
    if (Object.keys(errors).length > 0) {
      alerts.genericError("There are errors in the form, please check and try again")
      return
    } else {
      await onSubmit(data)
    }
  })

  return (
    <div>
      <Grid
        gap={8}
        css={css`
          max-width: 800px;
        `}
      >
        <Fields.Text
          label="Company Name"
          value={data.company_name}
          onChange={company_name => handleChange({ company_name })}
          placeholder={placeholders.companyName}
          error={errors.company_name}
        />
        <Fields.Text
          label="Name"
          value={data.name}
          onChange={name => handleChange({ name })}
          placeholder={placeholders.name}
          error={errors.name}
          showRequiredStar
        />
        <Fields.Email
          label="Email"
          value={data.email ?? ""}
          onChange={email => handleChange({ email })}
          error={errors.email}
          showRequiredStar
        />
        <Fields.Phone
          label="Phone"
          value={data.phone ?? ""}
          onChange={phone => handleChange({ phone })}
          error={errors.phone}
          showRequiredStar
        />
        <Fields.TextArea
          label="Notes"
          value={data.notes ?? ""}
          placeholder={placeholders.notes}
          onChange={notes => handleChange({ notes })}
        />
        <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
          <Fields.Text
            label="Source"
            value={data.source}
            placeholder={placeholders.marketingSource}
            onChange={source => handleChange({ source })}
          />
          <FieldsTextOptionsSelect
            label="Status"
            value={data.status}
            onChange={status => handleChange({ status: status ?? undefined })}
            options={["Open", "Closed"]}
          />
        </Grid>
        <CustomerSelectList2
          label="Customer"
          value={data.customer?.id}
          onChange={id => handleChange({ customer: id ? { id } : null })}
          isClearable
        />
        {!disableBranchSelection && (
          <BranchSelect
            label="Branch"
            onChange={branch => handleChange({ branch })}
            value={data.branch ?? undefined}
          />
        )}
        <FieldGroup name="Address">
          <Grid gap={8}>
            <Fields.Text
              label="Address Line 1"
              value={data.address?.line_1}
              placeholder={placeholders.line_1}
              onChange={line_1 => handleChange({ address: { ...data.address, line_1 } })}
            />
            <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
              <Fields.Text
                label="Address Line 2"
                placeholder={placeholders.line_2}
                value={data.address?.line_2}
                onChange={line_2 => handleChange({ address: { ...data.address, line_2 } })}
              />
              <Fields.Text
                label="Address Line 3"
                value={data.address?.line_3}
                onChange={line_3 => handleChange({ address: { ...data.address, line_3 } })}
              />
            </Grid>
            <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
              <Fields.Text
                label="City"
                placeholder={placeholders.city}
                value={data.address?.city}
                onChange={city => handleChange({ address: { ...data.address, city } })}
              />
              <Fields.Text
                label="County"
                placeholder={placeholders.county}
                value={data.address?.county}
                onChange={county => handleChange({ address: { ...data.address, county } })}
              />
            </Grid>
            <FieldsTextOptionsSelect
              label="Country"
              options={countries.map(c => c.name)}
              value={data.address?.country}
              onChange={country =>
                handleChange({ address: { ...data.address, country: country ?? "" } })
              }
            />
            <Fields.Text
              label="Postcode"
              placeholder={placeholders.postcode}
              value={data.address?.postcode}
              onChange={postcode => handleChange({ address: { ...data.address, postcode } })}
            />
          </Grid>
        </FieldGroup>
        <ButtonsWrapper>
          <BaseButton
            {...(handleSubmit.running
              ? buttonConfigs.formSaveButtonSaving
              : buttonConfigs.formSaveButton)}
            onClick={handleSubmit}
          />
          {actions}
        </ButtonsWrapper>
      </Grid>
    </div>
  )
}

function getMutationData(data: IData) {
  console.log(data)
  const { id, customer, branch, address, ...rest } = data

  const { id: addressId, ...addressData } = address ?? {}
  const addressMutation = address?.id
    ? { update: { id: addressId!, ...addressData } }
    : { create: addressData }
  const mutationData = {
    ...rest,
    address: addressMutation,
    customer: { set: customer?.id ?? null },
    branch: { set: branch?.id ?? null },
  }

  if (id) {
    return {
      update: {
        id,
        ...mutationData,
      },
    }
  } else {
    return {
      create: mutationData,
    }
  }
}

interface ITextOptionsSelectProps {
  options: string[] | readonly string[]
  isClearable?: boolean
  label?: IComponentProp<typeof Fields.Select, "label">
  value?: string
  disabled?: boolean
  placeholder?: string
  menuPortalTarget?: HTMLElement | null
  onChange(value: string | null): void
}

export function FieldsTextOptionsSelect({
  options,
  isClearable = true,
  label,
  value,
  disabled,
  onChange,
  placeholder,
  menuPortalTarget,
}: ITextOptionsSelectProps) {
  function opt(opt: string) {
    return { value: opt, label: titleize(opt) }
  }

  return (
    <Fields.Select2
      value={value ? opt(value) : undefined}
      label={label}
      onChange={opt => onChange(opt?.value ?? null)}
      disabled={disabled}
      options={(options as string[]).map(opt)}
      valueExtractor={(opt?: { label: string; value: string }) => opt?.value ?? undefined}
      isClearable={isClearable}
      placeholder={placeholder}
      menuPortalTarget={menuPortalTarget}
    />
  )
}

LeadsForm.getMutationData = getMutationData
